// Country (currency) and language picker (snippets/localization.liquid). Each toggle opens its list; picking an option
// fills the hidden field and sends Shopify's localization form (the page reloads in that country / language).
// The floating tab moves to <body> so the header's sticky / transform styles never move it.
if (!customElements.get('localization-picker')) {
  customElements.define('localization-picker', class extends HTMLElement {
    connectedCallback() {
      if (this.dataset.place === 'floating' && this.parentElement !== document.body) {
        document.querySelectorAll(`body > localization-picker[data-place="floating"]`).forEach((old) => old.remove());
        document.body.append(this);
        return;
      }
      if (this.dataset.place === 'floating') this.liftAboveStickyBar();
      if (this.ready) return;
      this.ready = true;
      this.addEventListener('click', (event) => this.onClick(event));
      this.addEventListener('input', (event) => this.onSearch(event));
      this.addEventListener('keydown', (event) => { if (event.key === 'Escape') this.close(true); });
      this.onOutside = (event) => { if (!this.contains(event.target)) this.close(); };
      document.addEventListener('click', this.onOutside);
    }

    disconnectedCallback() {
      if (this.parentElement) return;
      document.removeEventListener('click', this.onOutside);
    }

    onClick(event) {
      const toggle = event.target.closest('[data-loc-toggle]');
      if (toggle) {
        const open = toggle.getAttribute('aria-expanded') !== 'true';
        this.close();
        if (open) this.open(toggle);
        return;
      }
      const option = event.target.closest('[data-loc-value]');
      if (!option) return;
      const form = this.querySelector('form');
      form.querySelector(`input[name="${option.dataset.locInput}"]`).value = option.dataset.locValue;
      form.submit();
    }

    open(toggle) {
      const panel = this.querySelector(`#${CSS.escape(toggle.getAttribute('aria-controls'))}`);
      // a picker printed after the first one gets its list from it on first open (the list is printed once per page)
      panel.querySelectorAll('[data-loc-copy]').forEach((list) => {
        if (list.children.length) return;
        const source = document.getElementById(list.dataset.locCopy)?.querySelector('.loc__list');
        if (!source) return;
        list.innerHTML = source.innerHTML;
        list.querySelectorAll('li[hidden]').forEach((item) => { item.hidden = false; });
      });
      toggle.setAttribute('aria-expanded', 'true');
      panel.hidden = false;
      // open upwards when there is no room below (floating tab, bottom of the menu)
      panel.dataset.up = String(toggle.getBoundingClientRect().bottom + 320 > window.innerHeight && toggle.getBoundingClientRect().top > 320);
      // inside the menu the list opens in place: bring it into view in the scrolling menu
      if (this.dataset.place === 'menu') requestAnimationFrame(() => panel.scrollIntoView({ block: 'nearest', behavior: 'smooth' }));
      const search = panel.querySelector('[data-loc-search]');
      (search || panel.querySelector('[aria-current="true"]') || panel.querySelector('[data-loc-value]'))?.focus();
    }

    close(refocus) {
      this.querySelectorAll('[data-loc-toggle][aria-expanded="true"]').forEach((toggle) => {
        toggle.setAttribute('aria-expanded', 'false');
        this.querySelector(`#${CSS.escape(toggle.getAttribute('aria-controls'))}`).hidden = true;
        if (refocus) toggle.focus();
      });
    }

    // The sticky add-to-cart bar also sits at the bottom of the screen: while it shows, the tab moves up above it.
    liftAboveStickyBar() {
      const update = () => {
        const bar = document.querySelector('.sticky-cart.is-visible:not([data-position="top"])');
        this.style.setProperty('--loc-lift', `${bar ? bar.offsetHeight : 0}px`);
      };
      const watched = new WeakSet();
      const bars = new MutationObserver(update);
      const watch = () => document.querySelectorAll('.sticky-cart').forEach((bar) => { if (!watched.has(bar)) { watched.add(bar); bars.observe(bar, { attributes: true, attributeFilter: ['class'] }); } update(); });
      new MutationObserver(watch).observe(document.body, { childList: true }); // the bar is moved to <body> when it starts
      watch();
      window.addEventListener('resize', update);
    }

    onSearch(event) {
      if (!event.target.matches('[data-loc-search]')) return;
      const panel = event.target.closest('[data-loc-panel]');
      const term = event.target.value.trim().toLowerCase();
      let shown = 0;
      panel.querySelectorAll('.loc__list li').forEach((item) => {
        const match = !term || item.textContent.toLowerCase().includes(term);
        item.hidden = !match;
        if (match) shown += 1;
      });
      panel.querySelector('[data-loc-empty]').hidden = shown > 0;
    }
  });
}
